import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { keyframes } from '@mui/material/styles'

import {
  useLoiBurndown,
  useLoiDashboard,
  useLoiInsights,
  useLoiInstruments,
  useLoiRecommendations,
  useLoiTeamNotes,
  useLoiTopAnswers,
} from '@/features/campaigns/api/loi-dashboard-queries'
import { formatInstrumentDashboardLastUpdated } from '@/features/campaigns/lib/insight-display'
import { useDashboardLastUpdatedIso } from '@/features/campaigns/lib/use-dashboard-last-updated'
import { RiRefreshLine } from '@/shared/icons/remix'

const refreshSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

interface Props {
  loiId: string
}

export function AppShellLoiDashboardStatus({ loiId }: Readonly<Props>) {
  const { data: summary, isFetching: summaryFetching, refetch: refetchSummary, dataUpdatedAt } =
    useLoiDashboard(loiId)
  const { isFetching: instrumentsFetching, refetch: refetchInstruments } = useLoiInstruments(
    loiId,
    'all',
  )
  const { isFetching: burndownFetching, refetch: refetchBurndown } = useLoiBurndown(loiId, 'weekly')
  const { isFetching: topAnswersFetching, refetch: refetchTopAnswers } = useLoiTopAnswers(loiId)
  const { isFetching: insightsFetching, refetch: refetchInsights } = useLoiInsights(loiId)
  const { isFetching: recommendationsFetching, refetch: refetchRecommendations } =
    useLoiRecommendations(loiId)
  const { isFetching: teamNotesFetching, refetch: refetchTeamNotes } = useLoiTeamNotes(loiId)

  const lastUpdatedIso = useDashboardLastUpdatedIso(
    summary?.lastUpdatedAt,
    dataUpdatedAt,
    loiId,
  )

  const isRefreshing =
    summaryFetching ||
    instrumentsFetching ||
    burndownFetching ||
    topAnswersFetching ||
    insightsFetching ||
    recommendationsFetching ||
    teamNotesFetching

  const onRefresh = () => {
    void Promise.all([
      refetchSummary(),
      refetchInstruments(),
      refetchBurndown(),
      refetchTopAnswers(),
      refetchInsights(),
      refetchRecommendations(),
      refetchTeamNotes(),
    ])
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexShrink: 0,
      }}
    >
      <Typography
        data-testid="loi-dashboard-last-updated"
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '-0.1504px',
          color: '#4A5565',
          whiteSpace: 'nowrap',
        }}
      >
        Last Update{' '}
        {formatInstrumentDashboardLastUpdated(lastUpdatedIso)}
      </Typography>
      <IconButton
        size="small"
        aria-label={isRefreshing ? 'Refreshing dashboard' : 'Refresh dashboard'}
        aria-busy={isRefreshing}
        data-testid="loi-dashboard-refresh"
        disabled={isRefreshing}
        onClick={onRefresh}
        sx={{
          width: 36,
          height: 36,
          color: '#4A5565',
          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
        }}
      >
        <Box
          component="span"
          aria-hidden
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            animation: isRefreshing ? `${refreshSpin} 0.9s linear infinite` : 'none',
          }}
        >
          <RiRefreshLine size={24} />
        </Box>
      </IconButton>
    </Box>
  )
}
